/**
 * AssetTree Mock Store
 * 트리 구조 데이터를 목업으로 제공합니다.
 * Iceberg 데이터의 FQN 패턴을 기반으로 생성:
 * - gold-iceberg.gold.g_turbo.{table}
 * - silver-iceberg.silver.g_turbo.{table}
 * - bronze-iceberg.bronze.g_turbo.{table}
 */
import type { OpenMetadataResponse, OpenMetadataSource } from '@/shared/api/openmetadata/types';

const createTreeSource = (
  id: string,
  name: string,
  entityType: string,
  serviceType?: string,
  count: number = 0
): OpenMetadataSource => ({
  id,
  name,
  displayName: name.charAt(0).toUpperCase() + name.slice(1),
  description: `${name} ${entityType}`,
  fullyQualifiedName: name,
  version: 1.0,
  updatedAt: Date.now(),
  updatedBy: 'system',
  entityType,
  serviceType,
  service: serviceType
    ? {
        id: `service-${name}`,
        type: serviceType,
        name,
        fullyQualifiedName: name,
        description: `${name} 서비스`,
        displayName: name.charAt(0).toUpperCase() + name.slice(1),
        deleted: false,
      }
    : undefined,
  owners: [],
  tags: [],
  deleted: false,
  numberOfObjects: count,
});

export const getInitialTreeMockData = (): OpenMetadataResponse => {
  return {
    took: 15,
    timed_out: false,
    _shards: {
      total: 1,
      successful: 1,
      skipped: 0,
      failed: 0,
    },
    hits: {
      total: {
        value: 0,
        relation: 'eq',
      },
      max_score: 0,
      hits: [],
    },
    aggregations: {
      'sterms#entityType': {
        buckets: [
          { key: 'table', doc_count: 24 },
          { key: 'database', doc_count: 3 },
          { key: 'databaseSchema', doc_count: 3 },
          { key: 'glossaryTerm', doc_count: 28 },
          { key: 'tag', doc_count: 12 },
        ],
      },
      'sterms#serviceType': {
        buckets: [{ key: 'iceberg', doc_count: 24 }],
      },
      'sterms#service.displayName.keyword': {
        buckets: [
          { key: 'gold-iceberg', doc_count: 6 },
          { key: 'silver-iceberg', doc_count: 6 },
          { key: 'bronze-iceberg', doc_count: 12 },
        ],
      },
    },
  };
};

export const getContainerServiceTypesMockData = (): OpenMetadataResponse => {
  return {
    took: 10,
    timed_out: false,
    _shards: { total: 1, successful: 1, skipped: 0, failed: 0 },
    hits: {
      total: { value: 0, relation: 'eq' },
      max_score: 0,
      hits: [],
    },
    aggregations: {
      'sterms#serviceType': {
        buckets: [],
      },
    },
  };
};

export const getServiceTypeChildrenMockData = (serviceType: string): OpenMetadataResponse => {
  const normalizedServiceType = serviceType.toLowerCase();

  if (serviceType !== 'databaseService' && normalizedServiceType !== 'iceberg') {
    return {
      took: 10,
      timed_out: false,
      _shards: { total: 1, successful: 1, skipped: 0, failed: 0 },
      hits: {
        total: { value: 0, relation: 'eq' },
        max_score: 0,
        hits: [],
      },
      aggregations: {
        'sterms#service.displayName.keyword': {
          buckets: [],
        },
      },
    };
  }

  // Iceberg 서비스 3개
  const icebergServices: OpenMetadataSource[] = [
    {
      ...createTreeSource('svc-gold-iceberg', 'gold-iceberg', 'service', 'databaseService', 1),
      displayName: 'gold-iceberg',
      description: 'Gold 데이터 레이어 - 정제된 분석용 데이터',
      fullyQualifiedName: 'gold-iceberg',
    },
    {
      ...createTreeSource('svc-silver-iceberg', 'silver-iceberg', 'service', 'databaseService', 1),
      displayName: 'silver-iceberg',
      description: 'Silver 데이터 레이어 - 전처리된 데이터',
      fullyQualifiedName: 'silver-iceberg',
    },
    {
      ...createTreeSource('svc-bronze-iceberg', 'bronze-iceberg', 'service', 'databaseService', 1),
      displayName: 'bronze-iceberg',
      description: 'Bronze 데이터 레이어 - 원시 데이터',
      fullyQualifiedName: 'bronze-iceberg',
    },
  ];

  return {
    took: 10,
    timed_out: false,
    _shards: { total: 1, successful: 1, skipped: 0, failed: 0 },
    hits: {
      total: { value: icebergServices.length, relation: 'eq' },
      max_score: 1.0,
      hits: icebergServices.map((source, i) => ({
        _index: 'service_index',
        _id: source.id,
        _score: 1.0 - i * 0.1,
        _source: source,
      })),
    },
    aggregations: {
      'sterms#service.displayName.keyword': {
        buckets: [
          { key: 'gold-iceberg', doc_count: 6 },
          { key: 'silver-iceberg', doc_count: 6 },
          { key: 'bronze-iceberg', doc_count: 12 },
        ],
      },
    },
  };
};

export const getDatabasesMockData = (serviceName: string): OpenMetadataResponse => {
  const databaseMap: Record<string, { name: string; displayName: string; schemaCount: number }> = {
    'gold-iceberg': { name: 'gold', displayName: 'Gold', schemaCount: 1 },
    'silver-iceberg': { name: 'silver', displayName: 'Silver', schemaCount: 1 },
    'bronze-iceberg': { name: 'bronze', displayName: 'Bronze', schemaCount: 1 },
  };

  const dbInfo = databaseMap[serviceName];

  if (!dbInfo) {
    return {
      took: 10,
      timed_out: false,
      _shards: { total: 1, successful: 1, skipped: 0, failed: 0 },
      hits: {
        total: { value: 0, relation: 'eq' },
        max_score: 0,
        hits: [],
      },
    };
  }

  const database: OpenMetadataSource = {
    ...createTreeSource(
      `db-${serviceName}-${dbInfo.name}`,
      dbInfo.name,
      'database',
      'databaseService',
      dbInfo.schemaCount
    ),
    displayName: dbInfo.displayName,
    description: `${dbInfo.displayName} 데이터베이스`,
    fullyQualifiedName: `${serviceName}.${dbInfo.name}`,
    service: {
      id: `svc-${serviceName}`,
      type: 'databaseService',
      name: serviceName,
      fullyQualifiedName: serviceName,
      description: `${serviceName} 서비스`,
      displayName: serviceName.includes('gold')
        ? 'Gold Iceberg'
        : serviceName.includes('silver')
          ? 'Silver Iceberg'
          : 'Bronze Iceberg',
      deleted: false,
    },
    databaseSchemas: [
      {
        deleted: false,
        displayName: 'g_turbo',
        name: 'g_turbo',
        id: `schema-${serviceName}-${dbInfo.name}-g_turbo`,
        type: 'databaseSchema',
        fullyQualifiedName: `${serviceName}.${dbInfo.name}.g_turbo`,
      },
    ],
  };

  return {
    took: 10,
    timed_out: false,
    _shards: { total: 1, successful: 1, skipped: 0, failed: 0 },
    hits: {
      total: { value: 1, relation: 'eq' },
      max_score: 1.0,
      hits: [
        {
          _index: 'database_index',
          _id: database.id,
          _score: 1.0,
          _source: database,
        },
      ],
    },
    aggregations: {
      'sterms#database.displayName': {
        buckets: [{ key: database.displayName, doc_count: 1 }],
      },
    },
  };
};

export const getSchemasMockData = (serviceName: string, databaseName: string): OpenMetadataResponse => {
  // 모든 Iceberg는 g_turbo 스키마 사용
  const schemaName = 'g_turbo';

  // 각 레이어별 테이블 개수
  const tableCountMap: Record<string, number> = {
    'gold-iceberg': 6,
    'silver-iceberg': 6,
    'bronze-iceberg': 12,
  };

  const tableCount = tableCountMap[serviceName] || 0;

  // Gold 레이어 테이블 목록
  const goldTables = [
    'gsp_ecms1_feature_preprocessed',
    'kmp_ecms_feature_preprocessed',
    'hanwha_impact_feature_preprocessed',
    'kmp_ecms_diagnosis_results',
    'gsp_ecms1_diagnosis_result',
    'hanwha_impact_diagnosis_results',
  ];

  // Silver 레이어 테이블 목록
  const silverTables = [
    'gsp_ecms1_waveform_preprocessed',
    'hanwha_impact_config',
    'kmp_ecms_config',
    'hanwha_impact_waveform_preprocessed',
    'gsp_ecms1_config',
    'kmp_ecms_waveform_preprocessed',
  ];

  // Bronze 레이어 테이블 목록
  const bronzeTables = [
    'kmp_ecms_bearings1',
    'gsc_bearings',
    'hanwha_impact_bearings1',
    'gsp_ecms1_chart',
    'kmp_ecms_trend',
    'gsp_dcs',
    'gsp_ecms1_bearings2',
    'gsp_ecms1_bearings1',
    'hanwha_impact_chart',
    'gsp_ecms1_trend',
    'hanwha_impact_trend',
    'kmp_ecms_chart',
  ];

  const tableListMap: Record<string, string[]> = {
    'gold-iceberg': goldTables,
    'silver-iceberg': silverTables,
    'bronze-iceberg': bronzeTables,
  };

  const tableNames = tableListMap[serviceName] || [];

  const tableList = tableNames.map((tableName) => ({
    deleted: false,
    displayName: tableName,
    name: tableName,
    id: `table-${serviceName}-${databaseName}-${schemaName}-${tableName}`,
    type: 'table',
    fullyQualifiedName: `${serviceName}.${databaseName}.${schemaName}.${tableName}`,
  }));

  const schema: OpenMetadataSource = {
    ...createTreeSource(
      `schema-${serviceName}-${databaseName}-${schemaName}`,
      schemaName,
      'databaseSchema',
      'databaseService',
      tableCount
    ),
    displayName: 'g_turbo',
    description: '가디원 터보 연구 & 개발에 사용되는 데이터 마트',
    fullyQualifiedName: `${serviceName}.${databaseName}.${schemaName}`,
    service: {
      id: `svc-${serviceName}`,
      type: 'databaseService',
      name: serviceName,
      fullyQualifiedName: serviceName,
      description: `${serviceName} 서비스`,
      displayName: serviceName.includes('gold')
        ? 'Gold Iceberg'
        : serviceName.includes('silver')
          ? 'Silver Iceberg'
          : 'Bronze Iceberg',
      deleted: false,
    },
    database: {
      id: `db-${serviceName}-${databaseName}`,
      type: 'database',
      name: databaseName,
      fullyQualifiedName: `${serviceName}.${databaseName}`,
      displayName: databaseName.charAt(0).toUpperCase() + databaseName.slice(1),
      deleted: false,
    },
    tables: tableList,
  };

  return {
    took: 10,
    timed_out: false,
    _shards: { total: 1, successful: 1, skipped: 0, failed: 0 },
    hits: {
      total: { value: 1, relation: 'eq' },
      max_score: 1.0,
      hits: [
        {
          _index: 'databaseschema_index',
          _id: schema.id,
          _score: 1.0,
          _source: schema,
        },
      ],
    },
    aggregations: {
      'sterms#databaseSchema.displayName': {
        buckets: [{ key: schema.displayName, doc_count: tableCount }],
      },
    },
  };
};

export const getTablesMockData = (
  _serviceName: string,
  _databaseName: string,
  _schemaName: string
): OpenMetadataResponse => {
  // 테이블 목록은 schema의 tables 속성에 이미 포함되어 있으므로 빈 응답 반환
  return {
    took: 10,
    timed_out: false,
    _shards: { total: 1, successful: 1, skipped: 0, failed: 0 },
    hits: {
      total: { value: 0, relation: 'eq' },
      max_score: 0,
      hits: [],
    },
  };
};
